import {PHASE} from '@zzng/common/constants/phase';
import {DIGITAL_CARD, SCHEME} from '@zzng/common/constants/scheme';
import {openExternalWebview} from '@zzng/common/scheme/web';
import {isIOS} from '@zzng/common/utils/agent';
import {appendQueryParamToUrl} from '@zzng/common/utils/common';
import {openLandingUrl} from '@zzng/common/utils/openLandingUrl';
import {executeAppScheme} from '@zzng/common/utils/scheme';

import {ROUTES} from '@/root/constants/routes';

import PageLayout from '@/shared/components/PageLayout';
import {PAGE_NAME, STATUS_BAR_COLOR} from '@/shared/constants/statusBarColor';
import {TIARA_ACTION, TIARA_PAGE_NAME} from '@/shared/constants/tiara';
import {CS_URL, PROPOSITION_URL} from '@/shared/constants/url';
import useAdvancedNavigate from '@/shared/hooks/useAdvancedNavigate';
import useDarkMode from '@/shared/hooks/useDarkMode';
import useHistoryBackAOS from '@/shared/hooks/useHistoryBackAOS';
import usePageTransition from '@/shared/hooks/usePageTransition';
import {useStatusBarColorStore} from '@/shared/stores/statusBarColor';

type SettingMenuItem = {
  label: string;
  actionName: string;
};

type LinkItemProps = ({url: string; isExternal?: boolean} & SettingMenuItem) | ({scheme: string} & SettingMenuItem);

const CHANNEL_ID = {
  [PHASE.LOCAL]: 235325,
  [PHASE.SANDBOX]: 235325,
  [PHASE.CBT]: 399467783,
  [PHASE.REAL]: 399467783
};

const CARD_USAGES = [
  {
    label: '제출 이력',
    url: DIGITAL_CARD.PRESENT_HISTORY,
    actionName: TIARA_ACTION.PRESENT_HISTORY_CLICK
  }
] satisfies LinkItemProps[];

const SERVICE_GUIDES = [
  {
    label: '톡디지털카드 채널',
    actionName: TIARA_ACTION.DIGITALCARD_CHANNEL_CLICK,
    scheme: appendQueryParamToUrl(`${SCHEME.CHANNEL_HOME}/${CHANNEL_ID[TARGET]}`, {
      ...(isIOS && {dismiss_current_modal: false})
    })
  },
  {label: '고객센터', url: CS_URL, actionName: TIARA_ACTION.CS_CLICK, isExternal: true},
  {label: '제휴 제안', url: PROPOSITION_URL, actionName: TIARA_ACTION.PROPOSITION_CLICK, isExternal: true}
] satisfies LinkItemProps[];

const LinkItem = ({actionName, label, ...rest}: LinkItemProps) => {
  const onClickButton = () => {
    if ('scheme' in rest) {
      executeAppScheme(rest.scheme);
      return;
    }
    if (rest.isExternal) {
      openExternalWebview(rest.url, {isCurrentWebviewClose: false});
      return;
    }
    openLandingUrl(rest.url);
  };

  return (
    <li role="listitem">
      <button type="button" className="btn_list" onClick={onClickButton} data-tiara-action-name={actionName}>
        <span className="txt_btn">{label}</span>
      </button>
    </li>
  );
};

const HomeSettingPage = () => {
  const {transitionTarget, leave} = usePageTransition<HTMLDivElement>();
  const {setColor} = useStatusBarColorStore();
  const {isDarkMode} = useDarkMode();
  const navigate = useAdvancedNavigate();

  const onClickBack = () => {
    const {dark, light} = STATUS_BAR_COLOR[PAGE_NAME.HOME];
    setColor(isDarkMode ? dark : light);
    leave({onAnimationEnd: () => navigate(ROUTES.HOME, {keepSearchParams: true})});
  };

  useHistoryBackAOS(() => {
    onClickBack();
  });

  return (
    <PageLayout
      tiara={{pageName: TIARA_PAGE_NAME.HOME_SETTING, pageAction: TIARA_ACTION.HOME_SETTING_VIEW}}
      className="container-setting slide_container"
      statusBarColor={{default: STATUS_BAR_COLOR[PAGE_NAME.DEFAULT]}}
      headerProps={{title: '설정', buttons: [{type: 'BACK', onClick: onClickBack}]}}
      ref={transitionTarget}
    >
      <div className="group_list type_setting">
        <div className="inner_g">
          <strong className="tit_setting">카드 활용</strong>
          <ul className="list_g" role="list">
            {CARD_USAGES.map((usage) => (
              <LinkItem key={usage.label} {...usage} />
            ))}
          </ul>
          <strong className="tit_setting">서비스 안내</strong>
          <ul className="list_g" role="list">
            {SERVICE_GUIDES.map((guide) => (
              <LinkItem key={guide.label} {...guide} />
            ))}
          </ul>
        </div>
      </div>
    </PageLayout>
  );
};

export default HomeSettingPage;
